import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatService } from '@services/chat-service/chat-service';
import {
  MULTI_SELECT_SEPARATOR,
  QuizAnswerOutcome,
  QuizChatConfig,
  QuizChatMessage,
  QuizChatService,
  QuizOption,
  QuizQuestionLayout,
} from '@services/quiz-chat-service';
import { ConsultationQuizEngineService, QuizEngine } from '@services/consultation-quiz-engine-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { UserService } from '@services/user';
import { getQuizPhotoQuestionCopy, getRebrandedHairPhotoCopy, QuizPhotoQuestionCopy } from './quiz-photo-question-copy';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { ExperimentService } from '@services/experiment';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { CurrentComponentService } from '@services/current-component';
import { VoiceInputService } from '@services/voice-input-service';
import {
  getConcernSpecificQuestionCopy,
  CONCERN_SPECIFIC_QUESTION_IDS,
  FEMALE_HAIRFALL_STAGES_QUESTION_ID,
  MALE_HAIRFALL_STAGES_QUESTION_ID,
} from './quiz-concern-questions';
import { InputType, SingleSelectorService } from '@shared/single-selector/single-selector.service';
import { LocalStorageService } from '@services/local-storage-service';
import { NavigationService } from '@services/navigation-service';
import { CaptureContext, CaptureMedium, CapturePage, PhotoCaptureService, tryParseContext } from '@services/photo-capture-service';
import { QuizProgressHeaderComponent } from '@components/quiz/quiz-progress-header/quiz-progress-header.component';
import { QuizQuestionHeaderComponent } from '@components/quiz/quiz-question-header/quiz-question-header.component';
import { QuizOptionListComponent, QuizOptionListLayout } from '@components/quiz/quiz-option-list/quiz-option-list.component';
import { QuizImageOptionGridComponent } from '@components/quiz/quiz-image-option-grid/quiz-image-option-grid.component';
import { QuizTextAnswerComponent } from '@components/quiz/quiz-text-answer/quiz-text-answer.component';
import { QuizFooterCtaComponent } from '@components/quiz/quiz-footer-cta/quiz-footer-cta.component';
import { INSTANT_CHECKUP_ACTION_NAME } from '@services/quiz-chat-service/quiz-chat.types';
import { QuizPhotoReviewComponent } from '@components/quiz/quiz-photo-review/quiz-photo-review.component';
import { QuizPhotoUploadComponent } from '@components/quiz/quiz-photo-upload/quiz-photo-upload.component';
import { QuizPhotoUploadedStripComponent } from '@components/quiz/quiz-photo-uploaded-strip/quiz-photo-uploaded-strip.component';
import { QuizSecurePhotoNoteComponent } from '@components/quiz/quiz-secure-photo-note/quiz-secure-photo-note.component';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { QuizPhotoPairService } from '@services/quiz-photo-pair-service';
import { LoadingModule } from '@components/loading';
import { AppConfig } from 'client/app/app.config';

const CONSULTATION_SESSION_TYPE = 'consultationSession';
const ANALYTICS_PREFIX = 'consultation_quiz';
const WELCOME_QUESTION_ID = 'Welcome';
const AUTO_SKIP_QUESTION_IDS: readonly string[] = [WELCOME_QUESTION_ID, 'whatDoCureSkinMembersSay'];
// Image-option questions whose ordering conveys severity — each card gets a 1-based position badge.
const HAIRFALL_STAGE_QUESTION_IDS: readonly string[] = [
  MALE_HAIRFALL_STAGES_QUESTION_ID,
  FEMALE_HAIRFALL_STAGES_QUESTION_ID,
];
const CLICKED_BACK_EVENT = 'clickedBack';
const BOTH_PHOTOS_UPLOADED_TOAST_DURATION_MS = 3000;

@Component({
  selector: 'app-consultation-quiz',
  templateUrl: './consultation-quiz.html',
  standalone: true,
  imports: [
    QuizProgressHeaderComponent,
    QuizQuestionHeaderComponent,
    QuizOptionListComponent,
    QuizImageOptionGridComponent,
    QuizTextAnswerComponent,
    QuizFooterCtaComponent,
    QuizPhotoReviewComponent,
    QuizPhotoUploadComponent,
    QuizPhotoUploadedStripComponent,
    QuizSecurePhotoNoteComponent,
    ButtonCtaComponent,
    LoadingModule,
  ],
  providers: [ChatService, QuizChatService, ConsultationQuizEngineService, SingleSelectorService, QuizPhotoPairService],
})
export class ConsultationQuizComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly broadcast = inject(BroadcastService);
  private readonly connectionService = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly experimentService = inject(ExperimentService);
  private readonly eventLogger = inject(EventLoggerService);
  private readonly appBridge = inject(AppWebBridgeService);
  private readonly singleSelectorService = inject(SingleSelectorService);
  private readonly appConfig = inject(AppConfig);
  private readonly voiceInputService = inject(VoiceInputService);
  private readonly currentComponent = inject(CurrentComponentService);
  private readonly quizChatService = inject(QuizChatService);
  private readonly quizEngineService = inject(ConsultationQuizEngineService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly navigationService = inject(NavigationService);
  private readonly photoCapture = inject(PhotoCaptureService);
  private readonly instantCheckupService = inject(InstantCheckupService);
  private readonly userService = inject(UserService);
  readonly photoPair = inject(QuizPhotoPairService);

  readonly galleryUploadEnabled = signal(false);
  readonly bothPhotosUploadedToastUrls = signal<string[]>([]);
  private bothPhotosUploadedToastChecked = false;
  private bothPhotosUploadedToastTimer?: ReturnType<typeof setTimeout>;

  private readonly activeEngine = signal<QuizEngine>(this.quizChatService);

  get quizService(): QuizEngine {
    return this.activeEngine();
  }

  readonly QuizQuestionLayout = QuizQuestionLayout;
  readonly QuizOptionListLayout = QuizOptionListLayout;

  readonly consultationSession = signal<Table.ConsultationSession | null>(null);
  readonly showVoice = signal<boolean>(false);
  private readonly selectedIndices = signal<number[]>([]);
  private readonly typedText = signal<string>('');
  private readonly stepTotal = signal<number>(1);
  private readonly progressCountHidden = signal<boolean>(false);
  private readonly concernSpecificQuestionsEnabled = signal<boolean>(false);
  private readonly gridQuestionIds = signal<ReadonlySet<string>>(new Set());

  private readonly continueLabel = $localize`:@@continue:Continue`;
  private readonly skipContinueLabel = $localize`:@@skipAndContinue:Skip & Continue`;
  private readonly cameraIconClass = 'cs-icon-camera tw-text-500 tw-mt-1';

  private readonly syncQuestionForLanguageBanner = effect((): void => {
    this.quizService.questionText(); // reactive dep — do not remove (see CLAUDE.md)
    const questionId = this.quizService.currentQuestionId();
    const session = this.consultationSession();
    const regimenClass = session?.get('PrivateMainConcernClass');
    if (questionId && regimenClass) this.consultationSessionService.setLastBotQuestionId(regimenClass, questionId, session.id);
  });

  private readonly currentStep = computed(() => Math.min(this.quizService.questionNumber(), this.stepTotal()));
  readonly stepLabel = computed(() => (this.progressCountHidden() ? '' : `${this.currentStep()}/${this.stepTotal()}`));
  readonly progressPercent = computed(() => Math.round((this.currentStep() / this.stepTotal()) * 100));
  readonly showQuestion = computed(() => this.quizService.hasActiveQuestion() && !this.quizService.isCompleted());
  readonly showLoading = computed(() => !this.quizService.hasActiveQuestion() && !this.quizService.isCompleted());
  readonly inputsDisabled = computed(() => this.quizService.isThinking());
  readonly isSubmittingAnswer = computed(() => this.quizService.isThinking());
  readonly showBack = computed(() => !!this.quizService.canGoBack?.());
  readonly backDisabled = computed(() => this.inputsDisabled());
  readonly allSelectableOptions = computed(() => [...this.quizService.imageOptions(), ...this.quizService.buttonOptions()]);
  private readonly currentQuestionId = computed(() => {
    this.quizService.questionText(); // reactive dep — do not remove (see CLAUDE.md)
    return this.quizService.currentQuestionId();
  });
  readonly optionListType = computed(() => (
    this.gridQuestionIds().has(this.currentQuestionId())
      ? QuizOptionListLayout.Grid
      : QuizOptionListLayout.Stack
  ));
  readonly showImageOptionIndex = computed(() => HAIRFALL_STAGE_QUESTION_IDS.includes(this.currentQuestionId()));
  readonly restoredTextAnswer = computed<string | null>(() => {
    if (this.quizService.layout() !== QuizQuestionLayout.Text) return null;
    const answer = this.quizService.currentAnswer?.();
    if (typeof answer !== 'string' || !answer) return null;
    const noneOption = this.quizService.textCtaOption();
    if (answer === noneOption?.value || answer === noneOption?.text) return null;
    return answer;
  });

  readonly ctaLabel = computed(() => {
    if (this.quizService.layout() === QuizQuestionLayout.Text) {
      if (this.typedText().trim()) return this.continueLabel;
      return this.quizService.textCtaOption() ? this.skipContinueLabel : this.continueLabel;
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      return this.quizService.photoCtaOption()?.text || this.continueLabel;
    }
    return this.continueLabel;
  });
  readonly ctaIcon = computed(() => (
    this.quizService.layout() === QuizQuestionLayout.PhotoUpload ? this.cameraIconClass : ''
  ));
  readonly ctaDisabled = computed(() => {
    if (this.inputsDisabled()) return true;
    if (this.quizService.layout() === QuizQuestionLayout.Text) {
      return !this.typedText().trim() && !this.quizService.textCtaOption();
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      return false;
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoReview) {
      return false;
    }
    return this.selectedIndices().length === 0;
  });

  private user: Table.User;
  private completionHandled = false;

  constructor() {
    effect(() => {
      if (this.quizService.isCompleted()) this.handleCompletion();
    });
    effect(() => {
      if (this.quizService.requiresFallback()) this.navigateToLegacyChat();
    });
    effect(() => {
      this.quizService.questionText(); // reactive dep — do not remove (see CLAUDE.md)
      this.restorePageStateForCurrentQuestion();
    });
    effect(() => {
      void this.refreshGalleryUploadGate();
    });
    effect(() => {
      void this.validateAndShowBothPhotosUploadedToast();
    });
  }

  async ngOnInit(): Promise<void> {
    const screenLoadStart = Date.now();
    this.currentComponent.set(this);
    this.appBridge.setStatusBarColor('white');
    this.eventLogger.cleverTapEvent('impression', JSON.stringify({ name: ANALYTICS_PREFIX }));
    this.user = this.connectionService.getActingUser();
    const objectId: string = this.route.snapshot.params.id;
    const pendingSession = this.consultationSessionService.getPendingConsultationSession();
    const session = pendingSession?.id === objectId
      ? pendingSession
      : (await this.connectionService.fetchConsultationSessions({ where: { objectId } }))[0];
    this.consultationSession.set(session || null);
    if (!session) return;

    await this.experimentService.experimentsLoaded;
    const concernClass: string = session.get('PrivateMainConcernClass');
    this.stepTotal.set(this.experimentService.getConsultationQuizStepCount(concernClass));
    this.progressCountHidden.set(this.experimentService.isQuizProgressCountHidden());
    this.concernSpecificQuestionsEnabled.set(this.experimentService.shouldAskConcernSpecificQuestionFirst());
    this.gridQuestionIds.set(new Set(this.experimentService.getQuizGridQuestionIds()));
    await this.resolveVoiceVisibility();
    await this.startQuiz(session, concernClass);
    this.eventLogger.trackEvent('chat_screen_time', { timeInMillSec: Date.now() - screenLoadStart });
  }

  onSelectionChange(indices: number[]): void {
    this.selectedIndices.set(indices);
  }

  onButtonSelectionChange(indices: number[]): void {
    const offset = this.quizService.imageOptions().length;
    this.selectedIndices.set(indices.map((index) => index + offset));
  }

  onImageSelectionChange(index: number): void {
    this.selectedIndices.set([index]);
  }

  onTypedTextChange(value: string): void {
    this.typedText.set(value);
  }

  onOpenInstantCheckup(instantCheckupId: string): void {
    this.router.navigate([`/user/instantCheckup/${instantCheckupId}`]);
  }

  onCloseClicked(): void {
    this.eventLogger.cleverTapEvent(CLICKED_BACK_EVENT, JSON.stringify({ name: 'home' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  // Must stay async — app.component calls .then() on the result. See CLAUDE.md "Exit & back navigation".
  async handleBackPress(): Promise<boolean> {
    this.onCloseClicked();
    return true;
  }

  ngOnDestroy(): void {
    clearTimeout(this.bothPhotosUploadedToastTimer);
    this.currentComponent.remove(this);
  }

  async onContinue(): Promise<void> {
    if (this.quizService.layout() === QuizQuestionLayout.PhotoUpload) {
      await this.openPhotoAction();
      return;
    }
    if (this.quizService.layout() === QuizQuestionLayout.PhotoReview) {
      await this.quizService.submitAnswer('');
      return;
    }
    const message = this.buildAnswerMessage();
    if (!message) return;
    const questionNumber = this.quizService.questionNumber();
    const answeredQuestionId = this.currentQuestionId();
    const outcome = await this.quizService.submitAnswer(message);
    if (outcome !== QuizAnswerOutcome.Sent) return;
    this.consultationSessionService.triggerAiReportAnalysis(
      this.consultationSession()?.get('PrivateMainConcernClass'), answeredQuestionId,
    );
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `${ANALYTICS_PREFIX}_ans_q${questionNumber}` }));
  }

  /** Photo questions carry fixed copy in instruction mode; every other question keeps the tree's. */
  private readonly photoQuestionCopy = computed((): QuizPhotoQuestionCopy | undefined => {
    if (!this.experimentService.isQuizSkipPhotoInstructionsEnabled()) return undefined;
    const data = this.experimentService.isRebrandedHairInstructionsEnabled()
      ? getRebrandedHairPhotoCopy()[this.photoCheckupType()]
      : getQuizPhotoQuestionCopy()[this.currentQuestionId()];
    if (!data) return undefined;
    return this.userService.getUserGender() === this.appConfig.Shared.Gender.MALE ? data.male : data.female;
  });

  /** Fixed copy beats the tree's title: photo questions in instruction mode, concern-specific questions always. */
  readonly questionHeading = computed(() => {
    const concernCopy = this.concernSpecificQuestionsEnabled()
      ? getConcernSpecificQuestionCopy()[this.currentQuestionId()]
      : undefined;
    return this.photoQuestionCopy()?.title ?? concernCopy ?? this.quizService.questionText();
  });

  readonly questionSubText = computed(() => this.photoQuestionCopy()?.subText ?? this.quizService.questionSubText());

  private readonly photoCheckupType = computed(() => {
    const option = this.quizService.photoCtaOption();
    return option ? `${this.buildCaptureContext(option, {})?.tag ?? ''}`.toUpperCase() : '';
  });

  readonly photoInstructionImageUrl = computed<string | undefined>(() => {
    if (!this.experimentService.isQuizSkipPhotoInstructionsEnabled()) return undefined;
    return this.instantCheckupService.getPhotoInstructionImageUrl(
      this.photoCheckupType(), this.userService.getUserGender(), this.connectionService.currentWebSiteLanguage,
      this.experimentService.isRebrandedHairInstructionsEnabled(),
    );
  });

  /** Artwork is part of the mode: without it the card keeps the CTA and the note, so the page must not. */
  readonly isPhotoInstructionMode = computed(() => this.quizService.layout() === QuizQuestionLayout.PhotoUpload
    && this.experimentService.isQuizSkipPhotoInstructionsEnabled()
    && !!this.photoInstructionImageUrl());

  async onOpenPairPhoto(): Promise<void> {
    const photo = this.photoPair.alternatePhoto();
    if (!photo) return;
    const checkupId = await this.photoPair.resolveCheckupId(photo);
    if (checkupId) this.onOpenInstantCheckup(checkupId);
  }

  async onGalleryUpload(): Promise<void> {
    await this.launchPhotoAction(CaptureMedium.GALLERY);
  }

  /**
   * Camera and gallery resolve the same target: the legacy engine answers a chat row, so its id and
   * className must ride along for the server to attach the photo to that question.
   */
  private async launchPhotoAction(medium: CaptureMedium): Promise<void> {
    const option = this.quizService.photoCtaOption();
    if (!option) return;
    if (this.activeEngine() === this.quizEngineService) {
      const session = this.consultationSession();
      if (!session) return;
      await this.openInstantCheckupAction(option, { consultationSessionId: session.id }, undefined, undefined, medium);
      return;
    }
    const question = this.quizChatService.currentQuestion();
    if (!question) return;
    await this.openInstantCheckupAction(
      option,
      (question.get('context') as Record<string, unknown>) || {},
      question.id,
      question.className || '',
      medium,
    );
  }

  /** See client/app/consultation-quiz/CLAUDE.md for why each condition is needed. */
  private async validateAndShowBothPhotosUploadedToast(): Promise<void> {
    const questionId = this.currentQuestionId();
    const session = this.consultationSession();
    const regimenClass = this.toRegimenClass(session?.get('PrivateMainConcernClass'));
    if (this.bothPhotosUploadedToastChecked || !questionId || !regimenClass) return;
    if (!this.experimentService.getPhotoUploadedToastQuestionIds(regimenClass).includes(questionId)) return;
    if (!this.quizService.isAtFurthestQuestion()) return;
    this.bothPhotosUploadedToastChecked = true;
    const photos = await this.fetchPhotoPair(regimenClass);
    if (questionId !== this.currentQuestionId()) return;
    if (photos.length < 2) return;
    const bothTakenInThisConsultation = photos.every((photo) => photo.consultationSessionId === session.id);
    if (!bothTakenInThisConsultation) return;
    this.bothPhotosUploadedToastUrls.set(photos.map((photo) => photo.imagePath));
    clearTimeout(this.bothPhotosUploadedToastTimer);
    this.bothPhotosUploadedToastTimer = setTimeout(
      (): void => this.bothPhotosUploadedToastUrls.set([]), BOTH_PHOTOS_UPLOADED_TOAST_DURATION_MS,
    );
  }

  /** `session.get` is untyped; only a known class reaches the checkup lookups. */
  private toRegimenClass(value?: string): RegimenClass | undefined {
    const classes: RegimenClass[] = [this.appConfig.Shared.Regimen.Class.FACE, this.appConfig.Shared.Regimen.Class.HAIR];
    return classes.find((regimenClass): boolean => regimenClass === value);
  }

  private async fetchPhotoPair(
    regimenClass: RegimenClass,
  ): Promise<Array<{ consultationSessionId?: string, imagePath: string }>> {
    const userId = this.connectionService.getActingUser()?.id;
    if (!userId) return [];
    const grouped = await this.instantCheckupService.getCheckupsForClasses(userId, [regimenClass]);
    const byType = grouped[regimenClass] || {};
    return this.instantCheckupService.getCompleteProfileCheckupTypes(regimenClass)
      .map((type) => byType[type])
      .filter((checkup): checkup is NonNullable<typeof checkup> => !!checkup?.imagePath)
      .map((checkup) => ({
        consultationSessionId: checkup.extraData?.consultationSessionId, imagePath: checkup.imagePath,
      }));
  }

  private async refreshGalleryUploadGate(): Promise<void> {
    const option = this.quizService.photoCtaOption();
    const checkupType = this.photoCheckupType();
    if (!option || !checkupType || !this.isPhotoInstructionMode()) {
      this.galleryUploadEnabled.set(false);
      return;
    }
    const enabled = await this.instantCheckupService.isGalleryUploadEnabled(checkupType);
    if (checkupType !== this.photoCheckupType()) return;
    this.galleryUploadEnabled.set(enabled);
  }

  async onReviewCapture(checkupType: string): Promise<void> {
    const session = this.consultationSession();
    if (!session) return;
    const option = {
      action: 'ACTION',
      params: { actionName: INSTANT_CHECKUP_ACTION_NAME, queryParams: JSON.stringify({ tag: checkupType }) },
      text: '',
      type: 'BUTTON',
    };
    await this.openInstantCheckupAction(option as QuizOption, { consultationSessionId: session.id });
  }

  private async openPhotoAction(): Promise<void> {
    await this.launchPhotoAction(CaptureMedium.CAMERA);
  }

  private async openInstantCheckupAction(
    option: QuizOption, callerContext: Record<string, unknown>, messageId?: string, className?: string,
    medium: CaptureMedium = CaptureMedium.CAMERA,
  ): Promise<void> {
    if (!this.experimentService.isQuizSkipPhotoInstructionsEnabled()) {
      await this.singleSelectorService.openInAppAction(
        option as unknown as InputType, callerContext, undefined, messageId, className,
      );
      return;
    }
    const context = this.buildCaptureContext(option, callerContext, messageId, className);
    if (!context) {
      this.reportInvalidPhotoCta(option);
      return;
    }
    this.localStorageService.delete('fromSelfCheckupBanner');
    this.navigationService.persistRedirectUrl();
    await this.photoCapture.launch({
      tag: `${context.tag}`.toUpperCase(), context, medium, page: CapturePage.ConsultationQuiz,
    });
  }

  /**
   * Mirrors SingleSelectorService.openInAppActionInstantCheckup: the tree's bag is forwarded whole.
   * Undefined when that bag is unparsable — the old path threw there and captured nothing, and
   * defaulting to FULL_FACE would upload a face photo for a hair question.
   */
  private buildCaptureContext(
    option: QuizOption, callerContext: Record<string, unknown>, messageId?: string, className?: string,
  ): (CaptureContext & { tag: string }) | undefined {
    const raw = option.params?.queryParams;
    const optionParams = raw ? tryParseContext(raw) : {};
    if (!optionParams) return undefined;
    const context: CaptureContext = {
      tag: this.appConfig.Shared.InstantCheckup.Type.FULL_FACE,
      ...optionParams,
      ...callerContext,
      ...(messageId ? { messageId } : {}),
      ...(className ? { className } : {}),
    };
    return { ...context, tag: `${context.tag}` };
  }

  /** Reported from the action, never from a computed — the derivations above must stay side-effect free. */
  private reportInvalidPhotoCta(option: QuizOption): void {
    this.eventLogger.trackEvent('consultation_quiz_photo_cta_invalid_params', {
      questionId: this.currentQuestionId(), queryParams: option.params?.queryParams,
    });
    this.broadcast.broadcast('NOTIFY', { message: 'Could not start the camera. Please try again.' });
  }

  /** The concern-specific questions to ask first, in ask order. Empty when this session has none, enabled for frontendDrivenEngine only */
  private validateAndFindConcernSpecificQuestionIds(session: Table.ConsultationSession, concernClass: string): string[] {
    if (!this.experimentService.shouldAskConcernSpecificQuestionFirst()) return [];
    if (!this.experimentService.isFrontendDrivenQuizEnabled(concernClass)) return [];
    const concern: string = session.get('PrivateMainConcern');
    const questionIds = CONCERN_SPECIFIC_QUESTION_IDS[concern];
    if (!questionIds) return [];
    return this.userService.getUserGender() === this.appConfig.Shared.Gender.MALE
      ? questionIds.Male
      : questionIds.Female;
  }

  private async startQuiz(session: Table.ConsultationSession, concernClass: string): Promise<void> {
    const concernSpecificQuestionIds = this.validateAndFindConcernSpecificQuestionIds(session, concernClass);
    if (!concernSpecificQuestionIds.length) {
      await this.startEngine(session, concernClass);
      return;
    }
    await this.startWithConcernSpecificQuestions(session, concernClass, concernSpecificQuestionIds);
  }

  private async startWithConcernSpecificQuestions(
    session: Table.ConsultationSession, concernClass: string, concernSpecificQuestionIds: string[],
  ): Promise<void> {
    this.activeEngine.set(this.quizEngineService);
    this.photoPair.configure(this.quizEngineService);
    this.quizEngineService.configure({
      consultationSessionId: session.id,
      entries: [
        { questionId: WELCOME_QUESTION_ID },
        ...concernSpecificQuestionIds.map((questionId) => ({ questionId })),
      ],
      onEntriesExhausted: (): Promise<void> => this.startEngine(session, concernClass, concernSpecificQuestionIds),
      regimenClass: concernClass,
    });
    await this.quizEngineService.start();
  }

  private async startEngine(
    session: Table.ConsultationSession, concernClass: string, answeredFirstQuestionIds?: string[],
  ): Promise<void> {
    const engine = this.experimentService.isFrontendDrivenQuizEnabled(concernClass)
      ? this.quizEngineService : this.quizChatService;
    this.activeEngine.set(engine);
    this.photoPair.configure(engine);
    if (engine === this.quizEngineService) {
      this.quizEngineService.configure({
        answeredFirstQuestionIds, consultationSessionId: session.id, regimenClass: concernClass,
      });
      await this.quizEngineService.start();
      return;
    }
    this.quizChatService.configure(this.buildQuizConfig(session));
    await this.quizChatService.start();
  }

  async onBackClicked(): Promise<void> {
    if (!this.showBack() || this.backDisabled()) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      consultationSessionId: this.consultationSession()?.id,
      currentQuestionId: this.currentQuestionId(),
      name: `${ANALYTICS_PREFIX}_previous_question`,
    }));
    await this.quizService.goBack?.();
  }

  private restorePageStateForCurrentQuestion(): void {
    const answer = this.quizService.currentAnswer?.() ?? null;
    const answered = Array.isArray(answer) ? answer : [answer].filter((value) => value !== null);
    this.typedText.set(this.restoredTextAnswer() || '');
    const options = this.allSelectableOptions();
    this.selectedIndices.set(answered
      .map((value) => options.findIndex((option) => option.value === value || option.text === value))
      .filter((index) => index >= 0));
  }

  private buildAnswerMessage(): string {
    const layout = this.quizService.layout();
    if (layout === QuizQuestionLayout.Text) {
      return this.typedText().trim() || this.quizService.textCtaOption()?.text || '';
    }
    const options = this.allSelectableOptions();
    if (layout === QuizQuestionLayout.MultiSelect) {
      return this.selectedIndices()
        .map((index) => options[index]?.value || options[index]?.text || '')
        .filter((value) => !!value)
        .join(MULTI_SELECT_SEPARATOR);
    }
    const selected = options[this.selectedIndices()[0]];
    return selected?.text || selected?.value || '';
  }

  private buildQuizConfig(session: Table.ConsultationSession): QuizChatConfig {
    const concernClass: string = session.get('PrivateMainConcernClass');
    return {
      channel: {
        user: this.user,
        parent: session,
        parentField: CONSULTATION_SESSION_TYPE,
        fetchChats: (payload) => this.connectionService.findConsultationSessionChat(payload),
        replyUniqueIdPrefix: 'assistant_3_',
        pollingSeed: this.experimentService.getConsultationQuizBotReplySeed() ?? undefined,
        onBotReplyRetry: () => this.eventLogger.trackEvent('BOT_RESPONSE_RETRY', {
          userId: this.user?.id, assistant: this.quizChatService.currentQuestion(),
        }),
        onBotReplyReceived: (timeInMillSec: number) => this.eventLogger.trackEvent('BOT_RESPONSE_TIME', {
          userId: this.user?.id,
          timeInMillSec,
          lastResponseId: this.quizChatService.currentQuestion()?.id,
          lastQuestionId: this.quizChatService.currentQuestion()?.get('questionId'),
        }),
      },
      isCompletionMessage: (message: QuizChatMessage) => this.consultationSessionService.isRegimenAllocationMessage(message as Table.ConsultationSessionChat),
      skipQuestionIds: [...AUTO_SKIP_QUESTION_IDS],
      resumeNudge: () => this.connectionService.switchUserToState(
        `v4_${concernClass.toLowerCase()}:`,
        { consultationSessionId: session.id },
      ),
    };
  }

  private async resolveVoiceVisibility(): Promise<void> {
    this.showVoice.set(await this.voiceInputService.isMicAvailable());
  }

  private handleCompletion(): void {
    if (this.completionHandled) return;
    this.completionHandled = true;
    const session = this.consultationSession();
    const completionMessage = this.activeEngine() === this.quizChatService
      ? this.quizChatService.completionMessage() as Table.ConsultationSessionChat | null
      : null;
    const handled = this.consultationSessionService.handleRegimenAllocation(completionMessage, this.user, session);
    if (!handled) this.consultationSessionService.navigateToReport(session?.get('PrivateMainConcernClass'));
  }

  private navigateToLegacyChat(): void {
    const session = this.consultationSession();
    if (!session) return;
    this.router.navigate(['/chatV2', session.id], {
      queryParams: { type: CONSULTATION_SESSION_TYPE, fallback: true },
      replaceUrl: true,
    });
  }
}
